import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProtectedRoute } from '../components/auth/index.js';
import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import { ErrorState } from '../components/feedback/ErrorState/index.js';
import {
  BugBreakdownChart,
  BugBreakdownDetailModal,
  BugSeverityChart,
  EscapeTimingChart,
  EscapeTimingDetailModal,
} from '../components/features/reliability/index.js';
import type { BugBreakdownDetailPeriod } from '../components/features/reliability/index.js';
import { useKpiBugBreakdown, useKpiBugSeverity, useKpiBugEscapeTiming } from '../hooks/index.js';
import type { BugTrendUnit, EscapeTimingBin, KpiTeam } from '../types/api.js';
import { TEAM_OPTION_VALUES } from '@cortex/domain-product-innovation-lab-kpi-team-filter';
import { getTeamDisplayName } from './-kpi-team-filter.js';

/** @graph-connects none */
export const Route = createFileRoute('/reliability')({
  component: ReliabilityRoute,
});

/**
 * ルートガード。KPIと同じく内部メンバー（PI Div / admin）のみアクセス可能。
 * @graph-connects ProtectedRoute [calls] ページアクセス可否を判定しガードする
 */
function ReliabilityRoute() {
  return (
    <ProtectedRoute pageKey="reliability">
      <ReliabilityPage />
    </ProtectedRoute>
  );
}

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business バグ分析ページ。プロダクト全体のバグ健全性を監視する。全バグ内訳＋KPI捕捉率チャート・重大度別インシデントチャート（いずれもチャート単位で月次/週次・月曜始まりを切り替え可能）・エスケープ・タイミングチャート（90日窓の見逃し可視化。判定は KPI と同じ起票日−起因案件リリース日）とサマリー統計（集計単位に依らず常に月次）を表示する。チーム絞り込み（起因案件の開発リーダー所属）は全チャート・ドリルダウンに連動する。KPIページ（チーム評価）とは別レンズ
 * @graph-connects useKpiBugBreakdown [calls] バグ内訳データ取得フック呼び出し（集計単位別。サマリー統計用に月次固定でも呼ぶ）
 * @graph-connects useKpiBugSeverity [calls] 重大度別バグ件数取得フック呼び出し（集計単位別）
 * @graph-connects useKpiBugEscapeTiming [calls] エスケープ・タイミング取得フック呼び出し
 * @graph-connects getTeamDisplayName [calls] チーム表示名変換
 * @graph-connects BugBreakdownChart [calls] バグ内訳＋捕捉率チャート表示（月次/週次トグル付き）
 * @graph-connects BugSeverityChart [calls] 重大度別インシデントチャート表示（月次/週次トグル付き）
 * @graph-connects EscapeTimingChart [calls] エスケープ・タイミングチャート表示
 * @graph-connects BugBreakdownDetailModal [calls] 期間クリック時の期間別（月次/週次）バグ詳細ドリルダウンモーダル表示
 * @graph-connects EscapeTimingDetailModal [calls] ビンクリック時のビン別バグ詳細ドリルダウンモーダル表示
 * @graph-connects ProtectedRoute [calls] ページアクセスガード
 */
function ReliabilityPage() {
  const { t } = useTranslation();
  const [months, setMonths] = useState('12');
  // チーム絞り込み（全チャート・ドリルダウンに連動）。'all' は全体
  const [team, setTeam] = useState<KpiTeam>('all');
  // バグ内訳チャートの集計単位（月次/週次・月曜始まり）
  const [breakdownUnit, setBreakdownUnit] = useState<BugTrendUnit>('month');
  // 重大度別チャートの集計単位（月次/週次・月曜始まり）
  const [severityUnit, setSeverityUnit] = useState<BugTrendUnit>('month');
  // チャートでクリックされた期間（単位つき）。null の間はドリルダウンモーダル非表示
  const [detailPeriod, setDetailPeriod] = useState<BugBreakdownDetailPeriod | null>(null);
  // エスケープ・タイミングチャートでクリックされたビン。null の間はドリルダウンモーダル非表示
  const [detailBin, setDetailBin] = useState<EscapeTimingBin | null>(null);

  // 全体表示（all）のときは team パラメータを付けず、従来どおりのクエリキー・API 呼び出しにする
  const teamParam = team === 'all' ? {} : { team };

  const { data, isLoading, error, refetch } = useKpiBugBreakdown({
    months: Number(months),
    unit: breakdownUnit,
    ...teamParam,
  });
  // サマリー統計（最新月ベース）は集計単位の切り替えに影響されないよう常に月次で取得する
  // （バグ内訳チャートが月次のときはキャッシュを共有し追加リクエストは発生しない）
  const { data: statsData } = useKpiBugBreakdown({
    months: Number(months),
    unit: 'month',
    ...teamParam,
  });
  const { data: severityData, isLoading: isSeverityLoading } = useKpiBugSeverity({
    months: Number(months),
    unit: severityUnit,
    ...teamParam,
  });
  const { data: escapeData, isLoading: isEscapeLoading } = useKpiBugEscapeTiming({
    months: Number(months),
    ...teamParam,
  });

  const monthsOptions = [
    { value: '6', label: t('reliability.monthsUnit', { count: 6 }) },
    { value: '12', label: t('reliability.monthsUnit', { count: 12 }) },
    { value: '24', label: t('reliability.monthsUnit', { count: 24 }) },
  ];
  // 選択肢の並び・顔ぶれは kpi-team-filter の TEAM_OPTION_VALUES（5 画面共通の SSOT）に従う。
  // 判定は起因案件の開発リーダー所属（KPI と同じ）。
  const teamOptions = TEAM_OPTION_VALUES.map((value) => ({
    value,
    label: value === 'all' ? t('reliability.teamAll') : getTeamDisplayName(value),
  }));

  // サマリー統計は最新月（月次データの配列末尾）から算出
  const rows = statsData?.data ?? [];
  const latest = rows.length > 0 ? rows[rows.length - 1] : undefined;
  const totalBugs = latest?.total ?? 0;
  const latentBugs = latest?.latent ?? 0;
  const captureRatePct =
    latest && latest.captureRate !== null ? `${(latest.captureRate * 100).toFixed(1)}%` : '-';

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="animate-content-reveal">
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
            {t('reliability.title')}
          </h1>
          <p className="mt-1 text-[var(--color-text-secondary)]">{t('reliability.description')}</p>
        </div>
        <div className="flex gap-3 animate-content-reveal">
          <Select
            aria-label={t('reliability.teamFilter')}
            options={teamOptions}
            value={team}
            onChange={(e) => {
              setTeam(e.target.value as KpiTeam);
            }}
            size="sm"
          />
          <Select
            options={monthsOptions}
            value={months}
            onChange={(e) => {
              setMonths(e.target.value);
            }}
            size="sm"
          />
        </div>
      </div>

      {/* チーム絞り込み時は判定基準（起因案件の開発リーダー所属）と除外対象を明記する */}
      {team !== 'all' && (
        <p className="text-xs text-[var(--color-text-muted)] animate-content-reveal">
          {t('reliability.teamNote')}
        </p>
      )}

      {error && (
        <ErrorState
          title={t('feedback.errorTitle')}
          message={t('feedback.errorMessage')}
          onRetry={() => {
            void refetch();
          }}
        />
      )}

      {/* サマリー統計 */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 animate-content-reveal">
        <Card>
          <CardContent>
            <p className="text-sm text-[var(--color-text-muted)]">
              {t('reliability.stats.totalBugs')}
            </p>
            <p className="mt-1 text-2xl font-bold text-[var(--color-text-primary)] tabular-nums">
              {totalBugs.toLocaleString()}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <p className="text-sm text-[var(--color-text-muted)]">
              {t('reliability.stats.latentBugs')}
            </p>
            <p className="mt-1 text-2xl font-bold text-[var(--color-error)] tabular-nums">
              {latentBugs.toLocaleString()}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <p className="text-sm text-[var(--color-text-muted)]">
              {t('reliability.stats.captureRate')}
            </p>
            <p className="mt-1 text-2xl font-bold text-[var(--color-accent-primary)] tabular-nums">
              {captureRatePct}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* 全バグ内訳＋捕捉率チャート（月次/週次切り替え可能） */}
      <BugBreakdownChart
        data={data?.data ?? []}
        months={Number(months)}
        isLoading={isLoading}
        unit={breakdownUnit}
        onUnitChange={setBreakdownUnit}
        onPeriodClick={(period) => {
          setDetailPeriod({ unit: breakdownUnit, period });
        }}
      />

      {/* 重大度別インシデントチャート（月次/週次切り替え可能。期間クリックで同じドリルダウンを開く） */}
      <BugSeverityChart
        data={severityData?.data ?? []}
        months={Number(months)}
        isLoading={isSeverityLoading}
        unit={severityUnit}
        onUnitChange={setSeverityUnit}
        onPeriodClick={(period) => {
          setDetailPeriod({ unit: severityUnit, period });
        }}
      />

      {/* エスケープ・タイミングチャート（90日窓の内外を色分け） */}
      <EscapeTimingChart
        data={escapeData?.data ?? []}
        months={Number(months)}
        isLoading={isEscapeLoading}
        onBinClick={setDetailBin}
      />

      {/* 期間クリック→期間別バグ詳細ドリルダウンモーダル（チーム絞り込み連動） */}
      <BugBreakdownDetailModal
        open={detailPeriod !== null}
        onClose={() => {
          setDetailPeriod(null);
        }}
        period={detailPeriod}
        team={team}
      />

      {/* ビンクリック→エスケープ・タイミング詳細ドリルダウンモーダル（チーム絞り込み連動） */}
      <EscapeTimingDetailModal
        open={detailBin !== null}
        onClose={() => {
          setDetailBin(null);
        }}
        bin={detailBin}
        months={Number(months)}
        team={team}
      />
    </div>
  );
}
